import '../../css/welcome.css';

import {
    applicationScreens,
    welcomeAsset,
    welcomeMeta,
} from '@/components/welcome/experience/data';
import { useWelcomeAppearance } from '@/components/welcome/experience/use-welcome-appearance';
import { useWelcomeDialogs } from '@/components/welcome/experience/use-welcome-dialogs';
import { useWelcomeMotion } from '@/components/welcome/experience/use-welcome-motion';
import { WelcomeDialogs } from '@/components/welcome/experience/welcome-dialogs';
import { WelcomeExperience } from '@/components/welcome/experience/welcome-experience';
import { WelcomeFooter } from '@/components/welcome/experience/welcome-footer';
import { WelcomeHeader } from '@/components/welcome/experience/welcome-header';
import { dashboard, login, register } from '@/routes';
import { type SharedData } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { useCallback, useRef, useState } from 'react';

export default function Welcome() {
    const {
        auth,
        plans,
        canRegister = true,
    } = usePage<SharedData & { canRegister?: boolean }>().props;
    const isAuthenticated = Boolean(auth?.user);
    const { updateAppearance, resolved } = useWelcomeAppearance();
    const root = useRef<HTMLDivElement>(null);
    const { seekTo: seekImmediatelyTo, next } = useWelcomeMotion(root);
    const seekTo = useCallback(
        (value: number) => {
            seekImmediatelyTo(value, 'smooth');
            if (value === 0)
                root.current
                    ?.querySelector<HTMLElement>('.site-header .wordmark')
                    ?.focus({ preventScroll: true });
        },
        [seekImmediatelyTo],
    );
    const { openDialog, closeDialog } = useWelcomeDialogs(root);
    const [screen, selectScreen] = useState(applicationScreens[0]);
    const primary = isAuthenticated
        ? { href: dashboard().url, label: 'Acessar app' }
        : canRegister
          ? { href: register().url, label: 'Criar conta' }
          : { href: login().url, label: 'Entrar' };
    const access = isAuthenticated
        ? { href: dashboard().url, label: 'Acessar app' }
        : { href: login().url, label: 'Entrar' };

    return (
        <>
            <Head title={welcomeMeta.title}>
                <meta name="description" content={welcomeMeta.description} />
                <meta
                    name="theme-color"
                    content={resolved === 'dark' ? '#10272D' : '#F5F4EF'}
                />
            </Head>
            <div className="srv-welcome" ref={root}>
                <a className="skip-link" href="#experience">
                    Ir para o conteúdo
                </a>
                <div id="ambient-film" aria-hidden="true">
                    <video
                        id="ambient-video"
                        src={welcomeAsset('servion-ambient-motion.mp4')}
                        poster={welcomeAsset('servion-ambient-poster.jpg')}
                        width={1280}
                        height={720}
                        preload="auto"
                        muted
                        playsInline
                        tabIndex={-1}
                    />
                    <img
                        id="ambient-poster"
                        src={welcomeAsset('servion-ambient-poster.jpg')}
                        width={1280}
                        height={720}
                        alt=""
                    />
                </div>
                <WelcomeHeader
                    resolvedAppearance={resolved}
                    updateAppearance={updateAppearance}
                    primary={primary}
                    access={access}
                    showLogin={!isAuthenticated && canRegister}
                    seekTo={seekTo}
                    openDialog={openDialog}
                />
                <WelcomeExperience
                    primary={primary}
                    seekTo={seekTo}
                    next={next}
                    openDialog={openDialog}
                    screen={screen}
                    selectScreen={selectScreen}
                />
                <WelcomeFooter seekTo={seekTo} openDialog={openDialog} />
                <WelcomeDialogs
                    plans={plans}
                    primaryHref={primary.href}
                    primaryLabel={primary.label}
                    accessHref={access.href}
                    accessLabel={access.label}
                    screen={screen}
                    openDialog={openDialog}
                    closeDialog={closeDialog}
                    seekTo={seekTo}
                />
                <noscript>
                    <div className="noscript-message">
                        Ative o JavaScript para explorar a Servion.{' '}
                        <Link href={access.href}>Acessar a Servion</Link>.
                    </div>
                </noscript>
            </div>
        </>
    );
}
